import {
  AIR_ACCEL,
  GRAVITY,
  GROUND_ACCEL,
  JUMP_VELOCITY,
  MOVE_SPEED,
  PLAYER_HALF_WIDTH,
  PLAYER_HEIGHT,
  STEP_HEIGHT,
  TERMINAL_VELOCITY,
  VOXELS_PER_METER,
} from '../constants';
import { MatFlag } from '../voxel/materials';
import { AIR } from '../constants';
import type { VoxelWorld } from '../voxel/world';

/** Kinematic player body. Position is the center of the feet. */
export interface MoveState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  /** Remaining jetpack fuel in seconds. */
  jet: number;
  /** Height gained by the most recent auto-step (for camera smoothing). */
  stepped: number;
}

export interface MoveCmd {
  /** Strafe input, right positive, [-1, 1]. */
  mx: number;
  /** Forward input, [-1, 1]. */
  mz: number;
  yaw: number;
  jump: boolean;
  jet: boolean;
}

export interface MoveParams {
  speedMul: number;
  gravityMul: number;
  jumpMul: number;
  /** Cap on downward speed (gliding); defaults to terminal velocity. */
  maxFall?: number;
}

export const DEFAULT_MOVE_PARAMS: MoveParams = { speedMul: 1, gravityMul: 1, jumpMul: 1 };

export const JET_ACCEL = 48;
export const JET_MAX_UP = 7;
export const JET_FUEL = 1.2;

/** Voxels of an open liquid column a player sinks through (0.3 m: shin deep). */
export const WADE_DEPTH = 3;
/** Speed multiplier while wading. */
export const WADE_SPEED = 0.65;
/**
 * Jump multiplier from water. Pushing off the wading floor reaches the same height above the surface
 * as a normal jump does from land, so every bank climbable from the surface stays climbable.
 */
export const WADE_JUMP = 1.12;

/** Liquid material ids (set once from the world palette; both sides load the same content). */
const liquid = new Uint8Array(256);

export function setLiquidMaterials(flags: ArrayLike<number>): void {
  for (let i = 0; i < 256; i++) liquid[i] = (flags[i] ?? 0) & MatFlag.Liquid ? 1 : 0;
}

/**
 * Movement collision: world solidity, except that the top WADE_DEPTH voxels of a liquid column open
 * to the sky are passable. Bullets, explosions and structural support still treat liquid as solid.
 */
export function moveSolid(world: VoxelWorld, x: number, y: number, z: number): boolean {
  if (!world.isSolid(x, y, z)) return false;
  if (y < 0 || x < 0 || z < 0 || x >= world.sx || z >= world.sz) return true;
  if (!liquid[world.get(x, y, z)]) return true;
  for (let k = 1; k <= WADE_DEPTH; k++) {
    const above = world.get(x, y + k, z);
    if (above === AIR) return false;
    if (!liquid[above]) return true; // capped (under a dock or ice)
  }
  return true;
}

/** True when the voxel at the player's feet is liquid (wading or standing in a shallow pool). */
export function feetInLiquid(world: VoxelWorld, s: MoveState): boolean {
  return liquid[world.get(Math.floor(s.x * VOXELS_PER_METER), Math.floor(s.y * VOXELS_PER_METER + 1e-3), Math.floor(s.z * VOXELS_PER_METER))] === 1;
}

export function newMoveState(x = 0, y = 0, z = 0): MoveState {
  return { x, y, z, vx: 0, vy: 0, vz: 0, onGround: false, jet: 0, stepped: 0 };
}

export function copyMoveState(src: MoveState, dst: MoveState): MoveState {
  dst.x = src.x;
  dst.y = src.y;
  dst.z = src.z;
  dst.vx = src.vx;
  dst.vy = src.vy;
  dst.vz = src.vz;
  dst.onGround = src.onGround;
  dst.jet = src.jet;
  dst.stepped = src.stepped;
  return dst;
}

const EPS = 1e-3; // voxel units
const S = VOXELS_PER_METER;

/** True if a player box with feet at (x, y, z) overlaps no solid voxel. */
export function boxFree(world: VoxelWorld, x: number, y: number, z: number, hw = PLAYER_HALF_WIDTH, h = PLAYER_HEIGHT): boolean {
  const x0 = Math.floor((x - hw) * S + EPS);
  const x1 = Math.ceil((x + hw) * S - EPS) - 1;
  const y0 = Math.floor(y * S + EPS);
  const y1 = Math.ceil((y + h) * S - EPS) - 1;
  const z0 = Math.floor((z - hw) * S + EPS);
  const z1 = Math.ceil((z + hw) * S - EPS) - 1;
  for (let iy = y0; iy <= y1; iy++)
    for (let iz = z0; iz <= z1; iz++) for (let ix = x0; ix <= x1; ix++) if (moveSolid(world, ix, iy, iz)) return false;
  return true;
}

/**
 * Moves the box along one axis (0 = x, 1 = y, 2 = z) by `delta` meters, stopping at the first
 * solid voxel. Returns the distance actually moved.
 */
export function moveAxis(world: VoxelWorld, s: MoveState, axis: number, delta: number, hw = PLAYER_HALF_WIDTH, h = PLAYER_HEIGHT): number {
  if (delta === 0) return 0;
  const minX = (s.x - hw) * S;
  const maxX = (s.x + hw) * S;
  const minY = s.y * S;
  const maxY = (s.y + h) * S;
  const minZ = (s.z - hw) * S;
  const maxZ = (s.z + hw) * S;
  const d = delta * S;

  let a0: number, a1: number, b0: number, b1: number; // ranges of the two other axes
  let lo: number, hi: number; // box extent along the moving axis
  if (axis === 0) {
    a0 = Math.floor(minY + EPS);
    a1 = Math.ceil(maxY - EPS) - 1;
    b0 = Math.floor(minZ + EPS);
    b1 = Math.ceil(maxZ - EPS) - 1;
    lo = minX;
    hi = maxX;
  } else if (axis === 1) {
    a0 = Math.floor(minX + EPS);
    a1 = Math.ceil(maxX - EPS) - 1;
    b0 = Math.floor(minZ + EPS);
    b1 = Math.ceil(maxZ - EPS) - 1;
    lo = minY;
    hi = maxY;
  } else {
    a0 = Math.floor(minY + EPS);
    a1 = Math.ceil(maxY - EPS) - 1;
    b0 = Math.floor(minX + EPS);
    b1 = Math.ceil(maxX - EPS) - 1;
    lo = minZ;
    hi = maxZ;
  }

  const solidAt = (c: number, a: number, b: number): boolean => {
    if (axis === 0) return moveSolid(world, c, a, b);
    if (axis === 1) return moveSolid(world, a, c, b);
    return moveSolid(world, b, a, c);
  };
  const layerBlocked = (c: number): boolean => {
    for (let a = a0; a <= a1; a++) for (let b = b0; b <= b1; b++) if (solidAt(c, a, b)) return true;
    return false;
  };

  let moved = d;
  if (d > 0) {
    const first = Math.ceil(hi - EPS);
    const last = Math.ceil(hi + d) - 1;
    for (let c = first; c <= last; c++) {
      if (layerBlocked(c)) {
        moved = Math.max(0, c - EPS - hi);
        break;
      }
    }
  } else {
    const first = Math.floor(lo + EPS) - 1;
    const last = Math.floor(lo + d);
    for (let c = first; c >= last; c--) {
      if (layerBlocked(c)) {
        moved = Math.min(0, c + 1 + EPS - lo);
        break;
      }
    }
  }
  const m = moved / S;
  if (axis === 0) s.x += m;
  else if (axis === 1) s.y += m;
  else s.z += m;
  return m;
}

function moveHorizontal(world: VoxelWorld, s: MoveState, dx: number, dz: number): void {
  const mx = moveAxis(world, s, 0, dx);
  const mz = moveAxis(world, s, 2, dz);
  if (Math.abs(mx - dx) > 1e-9) s.vx = 0;
  if (Math.abs(mz - dz) > 1e-9) s.vz = 0;
}

/**
 * Advances a player body by one fixed step. Shared verbatim by server simulation and client
 * prediction, so it must stay deterministic for identical inputs and worlds.
 */
export function stepMove(world: VoxelWorld, s: MoveState, cmd: MoveCmd, dt: number, p: MoveParams = DEFAULT_MOVE_PARAMS): void {
  let mx = cmd.mx;
  let mz = cmd.mz;
  const len = Math.sqrt(mx * mx + mz * mz);
  if (len > 1) {
    mx /= len;
    mz /= len;
  }
  const sin = Math.sin(cmd.yaw);
  const cos = Math.cos(cmd.yaw);
  const wading = feetInLiquid(world, s);
  const speed = MOVE_SPEED * p.speedMul * (wading ? WADE_SPEED : 1);
  const wx = (-sin * mz + cos * mx) * speed;
  const wz = (-cos * mz - sin * mx) * speed;
  const hasInput = mx !== 0 || mz !== 0;

  if (s.onGround) {
    // Snappy ground movement: approach the wish velocity.
    let dvx = wx - s.vx;
    let dvz = wz - s.vz;
    const dl = Math.sqrt(dvx * dvx + dvz * dvz);
    const acc = GROUND_ACCEL * dt;
    if (dl > acc) {
      dvx *= acc / dl;
      dvz *= acc / dl;
    }
    s.vx += dvx;
    s.vz += dvz;
  } else if (hasInput) {
    // Quake-style air control: only adds speed along the wish direction, preserving knockback.
    const wl = Math.sqrt(wx * wx + wz * wz);
    const dirx = wx / wl;
    const dirz = wz / wl;
    const cur = s.vx * dirx + s.vz * dirz;
    const add = wl - cur;
    if (add > 0) {
      const acc = Math.min(AIR_ACCEL * dt * wl, add);
      s.vx += acc * dirx;
      s.vz += acc * dirz;
    }
  }

  if (cmd.jump && s.onGround) {
    s.vy = JUMP_VELOCITY * p.jumpMul * (wading ? WADE_JUMP : 1);
    s.onGround = false;
  }
  if (cmd.jet && s.jet > 0) {
    s.vy = Math.min(s.vy + JET_ACCEL * dt, JET_MAX_UP);
    s.jet = Math.max(0, s.jet - dt);
  }
  s.vy = Math.max(s.vy - GRAVITY * p.gravityMul * dt, -(p.maxFall ?? TERMINAL_VELOCITY));

  const wasGround = s.onGround;
  s.stepped = 0;

  // Vertical.
  const dy = s.vy * dt;
  const movedY = moveAxis(world, s, 1, dy);
  if (Math.abs(movedY - dy) > 1e-9) {
    if (dy < 0) s.onGround = true;
    s.vy = 0;
  } else {
    s.onGround = false;
  }

  // Horizontal, with auto-step over rubble and stairs.
  const dx = s.vx * dt;
  const dz = s.vz * dt;
  if (dx !== 0 || dz !== 0) {
    const sx0 = s.x;
    const sy0 = s.y;
    const sz0 = s.z;
    const vx0 = s.vx;
    const vz0 = s.vz;
    moveHorizontal(world, s, dx, dz);
    const blocked = s.vx !== vx0 || s.vz !== vz0;
    if (blocked && (s.onGround || wasGround)) {
      const flatX = s.x;
      const flatZ = s.z;
      const flatVx = s.vx;
      const flatVz = s.vz;
      const flatProgress = (flatX - sx0) * (flatX - sx0) + (flatZ - sz0) * (flatZ - sz0);
      // Try: rise, move, settle.
      s.x = sx0;
      s.y = sy0;
      s.z = sz0;
      s.vx = vx0;
      s.vz = vz0;
      const up = moveAxis(world, s, 1, STEP_HEIGHT);
      moveHorizontal(world, s, dx, dz);
      const down = moveAxis(world, s, 1, -up - 0.01);
      const stepProgress = (s.x - sx0) * (s.x - sx0) + (s.z - sz0) * (s.z - sz0);
      const landed = Math.abs(down - (-up - 0.01)) > 1e-9;
      if (landed && stepProgress > flatProgress + 1e-8 && s.y > sy0 - 1e-6) {
        s.stepped = s.y - sy0;
        s.onGround = true;
        s.vy = 0;
      } else {
        s.x = flatX;
        s.y = sy0;
        s.z = flatZ;
        s.vx = flatVx;
        s.vz = flatVz;
      }
    }
  }

  // Stick to the ground when walking down small ledges.
  if (wasGround && !s.onGround && s.vy <= 0) {
    const y0 = s.y;
    const probe = moveAxis(world, s, 1, -STEP_HEIGHT);
    if (Math.abs(probe + STEP_HEIGHT) > 1e-9) {
      s.onGround = true;
      s.vy = 0;
    } else {
      s.y = y0;
    }
  }
}
